import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Divider,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import {
  AddAPhotoOutlined,
  AddHomeOutlined,
  AlarmAddOutlined,
  ArrowForward,
  BuildCircleOutlined,
  CheckCircleOutline,
  EventNoteOutlined,
  HistoryOutlined,
  KeyboardArrowDown,
  LockOutlined,
  MenuBookOutlined,
  ReportProblemOutlined,
  RocketLaunchOutlined,
  UploadFileOutlined,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import APP_ROUTES from "../../../../Configurations/APP_ROUTES";
import DashCard, { EmptyState } from "../../dashboard/DashCard";
import { usePropertyDashboard } from "../../dashboard/PropertyDashboardContext";
import { DASH } from "../../dashboard/tokens";
import { useEntitlements } from "../../../../Hooks/useEntitlements";
import { UpgradeDialog } from "../../../../Components/PremiumGate/PremiumGate";
import { getMyTimeline, TimelineEvent } from "../../../../Services/timelineApi";
import { formatDay, todayDay } from "../../../../Utils/calendarDate";
import { repeatLabel } from "../../../../Services/maintenanceApi";
import { useMaintenance } from "../MaintenanceTab/MaintenanceContext";
import { activeByDue, countDueSoon, countOverdue, dueInfo } from "../MaintenanceTab/maintenanceSchedule";
import { CategoryIcon, DueChip, WarrantyIcon, expiryInfo } from "../MaintenanceTab/maintenanceUi";
import AdSlot from "./AdSlot";

type ActivityItem = {
  id: string;
  date: string;
  title: string;
  subtitle: string | null;
  source: "yours" | "public";
};

type Props = {
  /** Public-record last sale date, for visitors' and owners' activity. */
  lastSaleDate?: string | null;
  /** Bumped when Home History changes, to refresh Recent Activity. */
  historyVersion?: number;
  onAddPhotos?: () => void;
};

const ACTIVITY_SHOWN = 5;

/**
 * Home Status — the dashboard's landing tab (October 2026, Task 3): how the
 * home is doing, what's coming up, what happened recently, and an ad slot.
 */
const HomeStatus: React.FC<Props> = ({ lastSaleDate, historyVersion = 0, onAddPhotos }) => {
  const { isOwner, isAuthenticated, propertyRefID, propertyId, goTo, requestClaim, requestSignIn, isClaimed } =
    usePropertyDashboard();
  const { isPremium } = useEntitlements();
  const locked = isOwner && !isPremium;
  const { schedule, warranties, loading: maintenanceLoading } = useMaintenance();
  const [upsell, setUpsell] = useState(false);

  const today = useMemo(() => todayDay(), []);
  const items = useMemo(() => schedule?.items || [], [schedule]);
  const upcoming = useMemo(() => activeByDue(items), [items]);
  const overdue = countOverdue(items, today);
  const dueSoon = countDueSoon(items, today);
  // Maintenance and expiring warranties, soonest first.
  const reminders = useMemo(
    () =>
      [
        ...upcoming.map((item) => ({ key: item._id, date: item.dueDate || "", item, warranty: null })),
        ...warranties.map((w) => ({ key: `w-${w.id}`, date: w.date, item: null, warranty: w })),
      ].sort((a, b) => a.date.localeCompare(b.date)),
    [upcoming, warranties]
  );

  // ---- Recent activity: the owner's records + matched permits, and the
  // public-record sale.
  const [events, setEvents] = useState<TimelineEvent[] | null>(null);
  const [activityError, setActivityError] = useState(false);
  useEffect(() => {
    if (!isOwner) {
      setEvents([]);
      return;
    }
    let alive = true;
    setActivityError(false);
    getMyTimeline()
      .then((t) => {
        if (!alive) return;
        setEvents(
          t.events.filter(
            (e) => e.property && (e.property.mongoId === propertyRefID || e.property.propertyId === propertyId)
          )
        );
      })
      .catch(() => {
        if (alive) {
          setEvents([]);
          setActivityError(true);
        }
      });
    return () => {
      alive = false;
    };
  }, [isOwner, propertyRefID, propertyId, historyVersion]);

  const activity: ActivityItem[] = useMemo(() => {
    const out: ActivityItem[] = (events || []).map((e) => ({
      id: e.id,
      date: e.date,
      title: e.title,
      subtitle: [e.dateLabel, e.subtitle].filter(Boolean).join(" · ") || null,
      source: e.kind === "record" ? "yours" : "public",
    }));
    // Sale history was sign-in-only on the old page; keep it that way.
    if (isAuthenticated && lastSaleDate && !Number.isNaN(new Date(lastSaleDate).getTime())) {
      out.push({ id: "last-sale", date: lastSaleDate, title: "Home sold", subtitle: "Recorded sale", source: "public" });
    }
    return out.sort((a, b) => String(b.date).localeCompare(String(a.date)));
  }, [events, isAuthenticated, lastSaleDate]);

  return (
    <Stack spacing={4}>
      <StatusCard
        overdue={overdue}
        dueSoon={dueSoon}
        hasSchedule={upcoming.length > 0}
        hasRecords={(events || []).some((e) => e.kind === "record")}
        loading={isOwner && !locked && (maintenanceLoading || events === null)}
        locked={locked}
        onUpsell={() => setUpsell(true)}
        onAddPhotos={onAddPhotos}
      />

      <DashCard
        title="Upcoming Maintenance"
        icon={<EventNoteOutlined fontSize="small" />}
        subtitle={
          isOwner && !locked && reminders.length
            ? `${reminders.length} reminder${reminders.length === 1 ? "" : "s"}${overdue ? ` · ${overdue} overdue` : ""}`
            : undefined
        }
        action={
          isOwner && !locked ? (
            <Button size="small" onClick={() => goTo("maintenance")} endIcon={<ArrowForward />} sx={{ color: DASH.teal, fontWeight: 700 }}>
              View Maintenance
            </Button>
          ) : undefined
        }
        testId="upcoming-maintenance-card"
      >
        {!isOwner ? (
          <Typography variant="body2" sx={{ color: DASH.muted }}>
            Owners get a maintenance plan for this home — HVAC service, gutter cleaning, smoke-alarm checks — with
            reminders before anything's due.{" "}
            <ButtonBase onClick={isAuthenticated ? requestClaim : requestSignIn} sx={{ color: DASH.teal, fontWeight: 700, fontSize: "inherit", verticalAlign: "baseline" }}>
              {isAuthenticated ? (isClaimed ? "Own this home?" : "Claim it to start") : "Sign in to start"}
            </ButtonBase>
          </Typography>
        ) : locked ? (
          <Stack spacing={2}>
            {(schedule?.recommendations || []).slice(0, 2).map((r, i) => (
              <ButtonBase
                key={r.key}
                onClick={() => setUpsell(true)}
                sx={{ justifyContent: "flex-start", gap: 3, p: 3, borderRadius: "12px", border: `1px solid ${DASH.border}`, textAlign: "left" }}
                data-testid="upcoming-teaser"
              >
                <CategoryIcon category={r.category} />
                <Box sx={{ flex: 1, minWidth: 0, filter: i === 0 ? "none" : "blur(4px)" }}>
                  <Typography sx={{ fontWeight: 700 }}>{r.title}</Typography>
                  <Typography sx={{ fontSize: 13, color: DASH.muted }}>Recommended {repeatLabel(r.intervalMonths).toLowerCase()}</Typography>
                </Box>
                <LockOutlined sx={{ color: DASH.muted, fontSize: 18 }} />
              </ButtonBase>
            ))}
          </Stack>
        ) : maintenanceLoading && !schedule ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={22} sx={{ color: DASH.teal }} />
          </Stack>
        ) : reminders.length === 0 ? (
          <EmptyState
            icon={<AlarmAddOutlined sx={{ fontSize: 30 }} />}
            title="No reminders yet"
            body="Set up maintenance once and we'll keep track of what's due."
            action={
              <Button onClick={() => goTo("maintenance")} sx={{ color: DASH.teal, fontWeight: 700 }}>
                Set up maintenance
              </Button>
            }
            testId="upcoming-empty"
          />
        ) : (
          <Stack spacing={2}>
            {reminders.slice(0, 3).map(({ key, item, warranty }) => (
              <ButtonBase
                key={key}
                onClick={() => goTo(item ? "maintenance" : "home-history")}
                sx={{ justifyContent: "flex-start", gap: 3, p: 3, borderRadius: "12px", border: `1px solid ${DASH.border}`, textAlign: "left" }}
              >
                {item ? <CategoryIcon category={item.category} /> : <WarrantyIcon />}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700 }} noWrap>
                    {item ? item.title : `${warranty!.name} warranty`}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: DASH.muted }} noWrap>
                    {item
                      ? `${formatDay(item.dueDate)} · ${repeatLabel(item.intervalMonths)}`
                      : `${warranty!.label} · expires ${formatDay(warranty!.date)}`}
                  </Typography>
                </Box>
                <DueChip
                  info={item ? dueInfo(item.dueDate, today) : expiryInfo(dueInfo(warranty!.date, today))}
                />
              </ButtonBase>
            ))}
          </Stack>
        )}
        <Box sx={{ mt: 3 }}>
          <Link
            component={RouterLink}
            to={String(APP_ROUTES.HOME_MAINTENANCE_GUIDE.pathname)}
            sx={{ fontSize: 13.5, fontWeight: 700, color: DASH.teal, display: "inline-flex", alignItems: "center", gap: 0.5 }}
            data-testid="maintenance-guide-link"
          >
            <MenuBookOutlined sx={{ fontSize: 16 }} /> View Maintenance Guide
          </Link>
        </Box>
      </DashCard>

      <DashCard
        title="Recent Activity"
        icon={<HistoryOutlined fontSize="small" />}
        action={
          isOwner && activity.length > 0 ? (
            <Button size="small" onClick={() => goTo("home-history")} endIcon={<ArrowForward />} sx={{ color: DASH.teal, fontWeight: 700 }}>
              View All
            </Button>
          ) : undefined
        }
        testId="recent-activity-card"
      >
        {isOwner && events === null ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={22} sx={{ color: DASH.teal }} />
          </Stack>
        ) : activity.length === 0 ? (
          <EmptyState
            icon={<HistoryOutlined sx={{ fontSize: 30 }} />}
            title={activityError ? "Couldn't load recent activity" : "No activity yet"}
            body={
              isOwner
                ? "Records you add and permits on your address will show up here."
                : "This home's public record events will show up here."
            }
            action={
              isOwner ? (
                <Button onClick={() => goTo("home-history", "upload")} sx={{ color: DASH.teal, fontWeight: 700 }}>
                  Add your first record
                </Button>
              ) : undefined
            }
            testId="activity-empty"
          />
        ) : (
          <ActivityTimeline items={activity} locked={locked} onUpsell={() => setUpsell(true)} />
        )}
      </DashCard>

      <AdSlot
        placement="home-status-bottom"
        actionLabel={isOwner ? "Add to your history" : isClaimed ? "Learn more" : "Claim this home"}
        onAction={() => (isOwner ? goTo("home-history", "upload") : isClaimed ? goTo("home-history") : requestClaim())}
      />

      <UpgradeDialog
        open={upsell}
        onClose={() => setUpsell(false)}
        title="Make sure your home's in good shape"
        description="Premium gives you a maintenance plan with reminders, your full home history, and a place for every document."
      />
    </Stack>
  );
};

const ActivityTimeline: React.FC<{ items: ActivityItem[]; locked: boolean; onUpsell: () => void }> = ({
  items,
  locked,
  onUpsell,
}) => {
  const shown = items.slice(0, ACTIVITY_SHOWN);
  return (
    <Stack component="ol" sx={{ listStyle: "none", m: 0, p: 0 }} data-testid="activity-timeline">
      {shown.map((a, i) => {
        // Free owner: the two newest entries are a teaser; the rest blur.
        const blurred = locked && i >= 2;
        return (
          <Stack
            component="li"
            key={a.id}
            direction="row"
            spacing={3}
            onClick={blurred ? onUpsell : undefined}
            sx={{ position: "relative", pb: i === shown.length - 1 ? 0 : 4, cursor: blurred ? "pointer" : "default" }}
          >
            <Box sx={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", pt: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  border: `3px solid ${a.source === "yours" ? DASH.teal : DASH.gold}`,
                  bgcolor: "#fff",
                  zIndex: 1,
                }}
              />
              {i < shown.length - 1 && (
                <Box sx={{ position: "absolute", top: 18, bottom: -2, width: 2, bgcolor: DASH.border }} />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, filter: blurred ? "blur(4px)" : "none" }} aria-hidden={blurred || undefined}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: DASH.muted }}>{formatDay(a.date)}</Typography>
              <Typography sx={{ fontWeight: 700, color: "#1d2b2b" }} noWrap>
                {a.title}
              </Typography>
              {a.subtitle && (
                <Typography sx={{ fontSize: 13, color: DASH.muted }} noWrap>
                  {a.subtitle}
                </Typography>
              )}
            </Box>
            <Box sx={{ flexShrink: 0, pt: 2.5 }}>
              <SourceChip source={a.source} />
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
};

export const SourceChip: React.FC<{ source: "yours" | "public" }> = ({ source }) => (
  <Typography
    component="span"
    sx={{
      px: 2,
      py: 0.5,
      borderRadius: 50,
      fontSize: 11.5,
      fontWeight: 800,
      whiteSpace: "nowrap",
      bgcolor: source === "yours" ? DASH.tealTint2 : DASH.amberTint,
      color: source === "yours" ? DASH.teal : DASH.amber,
    }}
  >
    {source === "yours" ? "Your Record" : "Public Record"}
  </Typography>
);

/** "Your Home is in Good Shape" — and its honest variants. */
const StatusCard: React.FC<{
  overdue: number;
  dueSoon: number;
  hasSchedule: boolean;
  hasRecords: boolean;
  loading: boolean;
  locked: boolean;
  onUpsell: () => void;
  onAddPhotos?: () => void;
}> = ({ overdue, dueSoon, hasSchedule, hasRecords, loading, locked, onUpsell, onAddPhotos }) => {
  const { isOwner, isAuthenticated, isClaimed, apnStatus, goTo, requestClaim, requestSignIn } = usePropertyDashboard();
  const [menu, setMenu] = useState<HTMLElement | null>(null);

  let tone: "good" | "warn" | "start" | "visitor" = "good";
  let title = "Your Home is in Good Shape";
  let body = dueSoon
    ? `Nothing's overdue. ${dueSoon} reminder${dueSoon === 1 ? "" : "s"} coming up in the next 30 days.`
    : "Nothing's overdue, and nothing's due in the next 30 days.";

  if (!isOwner) {
    tone = "visitor";
    if (apnStatus === "PENDING") {
      title = "Your claim is under review";
      body = "Once it's approved, this becomes your home's control room — maintenance, records and documents in one place.";
    } else if (isClaimed) {
      title = "This home is claimed and protected";
      body = "Its owner keeps its records on SimplerHome. You can still see its public details.";
    } else {
      title = "Sign up to make sure your home's in good shape";
      body = "Claim this home free to lock it, then track its maintenance, records and documents in one place.";
    }
  } else if (locked) {
    tone = "start";
    title = "Make sure your home's in good shape";
    body = "Premium tracks maintenance, records and warranties so nothing slips through the cracks.";
  } else if (overdue > 0) {
    tone = "warn";
    title = "A few things need attention";
    body = `${overdue} maintenance item${overdue === 1 ? " is" : "s are"} overdue.`;
  } else if (!hasSchedule && !hasRecords) {
    tone = "start";
    title = "Let's start your home's record";
    body = "Add a repair you've had done, or set up maintenance reminders — it only takes a minute.";
  }

  const palette = {
    good: { bg: "linear-gradient(135deg, #eef8f3 0%, #ffffff 70%)", icon: <CheckCircleOutline />, iconBg: DASH.greenTint, iconFg: DASH.green },
    warn: { bg: "linear-gradient(135deg, #fff4e5 0%, #ffffff 70%)", icon: <ReportProblemOutlined />, iconBg: DASH.amberTint, iconFg: DASH.amber },
    start: { bg: `linear-gradient(135deg, ${DASH.cream} 0%, #ffffff 70%)`, icon: <RocketLaunchOutlined />, iconBg: DASH.tealTint2, iconFg: DASH.teal },
    visitor: { bg: `linear-gradient(135deg, ${DASH.cream} 0%, #ffffff 70%)`, icon: <AddHomeOutlined />, iconBg: DASH.tealTint2, iconFg: DASH.teal },
  }[tone];

  const add = (tab: Parameters<typeof goTo>[0], focus?: string) => {
    setMenu(null);
    if (locked) onUpsell();
    else goTo(tab, focus);
  };

  return (
    <Box
      sx={{ borderRadius: DASH.radius, border: `1px solid ${DASH.border}`, boxShadow: DASH.cardShadow, background: palette.bg, p: { xs: 5, md: 7 } }}
      data-testid="home-status-card"
      data-tone={loading ? "loading" : tone}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={4} alignItems={{ xs: "flex-start", sm: "center" }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            flexShrink: 0,
            borderRadius: "16px",
            bgcolor: palette.iconBg,
            color: palette.iconFg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& svg": { fontSize: 30 },
          }}
        >
          {loading ? <CircularProgress size={24} sx={{ color: palette.iconFg }} /> : palette.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: 21, md: 24 }, color: "#1d2b2b", lineHeight: 1.25 }} data-testid="home-status-title">
            {loading ? "Checking on your home…" : title}
          </Typography>
          {!loading && (
            <Typography sx={{ mt: 1, color: "#3a4646", fontSize: 15 }}>{body}</Typography>
          )}
        </Box>
      </Stack>

      <Stack direction="row" spacing={3} alignItems="center" sx={{ mt: 5, flexWrap: "wrap", rowGap: 2 }}>
        {isOwner ? (
          <>
            <Button
              variant="contained"
              onClick={(e) => (locked ? onUpsell() : setMenu(e.currentTarget))}
              endIcon={locked ? <LockOutlined /> : <KeyboardArrowDown />}
              sx={{ bgcolor: DASH.teal, "&:hover": { bgcolor: DASH.tealDark }, borderRadius: 2, px: 5, fontWeight: 700 }}
              data-testid="add-to-my-home"
            >
              Add to My Home
            </Button>
            <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
              <MenuItem onClick={() => add("home-history", "upload")}>
                <ListItemIcon><BuildCircleOutlined fontSize="small" /></ListItemIcon>
                Add a repair or improvement
              </MenuItem>
              <MenuItem onClick={() => add("maintenance", "add")}>
                <ListItemIcon><AlarmAddOutlined fontSize="small" /></ListItemIcon>
                Add a maintenance reminder
              </MenuItem>
              <MenuItem onClick={() => add("documents", "upload")}>
                <ListItemIcon><UploadFileOutlined fontSize="small" /></ListItemIcon>
                Upload a document
              </MenuItem>
              {onAddPhotos && <Divider />}
              {onAddPhotos && (
                <MenuItem
                  onClick={() => {
                    setMenu(null);
                    onAddPhotos();
                  }}
                >
                  <ListItemIcon><AddAPhotoOutlined fontSize="small" /></ListItemIcon>
                  Add photos
                </MenuItem>
              )}
            </Menu>
            {tone === "warn" ? (
              <Button onClick={() => goTo("maintenance")} sx={{ color: DASH.teal, fontWeight: 700 }}>
                Review maintenance
              </Button>
            ) : (
              <Button onClick={() => goTo("home-history")} sx={{ color: DASH.teal, fontWeight: 700 }} data-testid="see-full-history">
                See Full Home History
              </Button>
            )}
          </>
        ) : apnStatus === "PENDING" ? null : (
          <>
            <Button
              variant="contained"
              onClick={isAuthenticated ? requestClaim : requestSignIn}
              sx={{ bgcolor: DASH.teal, "&:hover": { bgcolor: DASH.tealDark }, borderRadius: 2, px: 5, fontWeight: 700 }}
              data-testid="status-claim-cta"
            >
              {!isAuthenticated ? "Sign up free" : isClaimed ? "I own this home" : "Claim this home"}
            </Button>
            <Button onClick={() => goTo("property-details")} sx={{ color: DASH.teal, fontWeight: 700 }}>
              See property details
            </Button>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default HomeStatus;
